<template>
  <div class="signup-wrapper">
    <div class="signup-content">
      <div class="signup-step-back">
        <v-icon color="#333333" size="22" @click="$router.go(-1)">
          mdi-arrow-left
        </v-icon>
      </div>
      <h3 class="signup-declarations-title">
        {{ $t("signup.profiling.declarations.title") }}
      </h3>

      <v-form class="signup-form" v-model="validForm">
        <div v-for="(otherDeclaration, index) in otherDeclarations" :key="index">
          <div class="other-declaration-text">
            <div class="other-declaration-question">
              {{
                $t(
                  `signup.profiling.otherDeclarations.${otherDeclaration.declaration}`,
                )
              }}
            </div>
          </div>
          <div class="other-declaration-option-container">
            <div
              v-for="(answerOption, index) in [true, false]"
              :key="index"
              class="other-declaration-option"
              :class="[
                signupDeclarations[otherDeclaration.declaration] === answerOption
                  ? 'selected-other-declaration'
                  : 'unselected-other-declaration',
              ]"
              @click="
                setProfilingCustomerDeclarations({
                  [otherDeclaration.declaration]: answerOption,
                })
              "
            >
              <span>
                {{
                  $t(`signup.profiling.otherDeclarations.answers.${answerOption}`)
                }}
              </span>
            </div>
          </div>

          <div v-if="otherDeclaration.declaration === 'isCrs' && signupDeclarations.isCrs">
            <div
              v-for="(tinValue, index) in tinValues"
              :key="index"
              class="tin-declaration"
            >
              <v-autocomplete
                :id="`tin-country-${index}`"
                v-model="tinValue.country"
                :items="countries"
                item-text="country"
                item-value="code"
                outlined
                rounded
                required
                append-icon="mdi-chevron-down"
                :placeholder="
                  $t('signup.profiling.otherDeclarations.tinDeclaration.country')
                "
                auto-select-first
                autocomplete="off"
                :rules="countryRules"
                hide-details
                :no-data-text="$t('base.noResults')"
                class="signup-input-field"
                :menu-props="{
                  rounded: 'lg',
                  contentClass: 'signup-custom-menu elevation-0',
                  offsetY: true,
                }"
              />

              <v-text-field
                :id="`tin-number-${index}`"
                v-model="tinValue.tin"
                outlined
                rounded
                required
                autocomplete="off"
                class="signup-input-field"
                :rules="mustCompleteRules"
                :ref="`tinNumber-${index}`"
                :placeholder="
                  $t('signup.profiling.otherDeclarations.tinDeclaration.tin')
                "
                :hide-details="
                  tinValue.tin !== null && !$refs[`tinNumber-${index}`].isFocused
                    ? validateRef($refs[`tinNumber-${index}`])
                    : ''
                "
              />
            </div>
            <div class="signup-tin-buttons-wrapper">
              <div>
                <NewButton
                  buttonClass="black-outlined"
                  id="signup-declarations-add-tin"
                  @on-click="addTin()"
                  :text="
                    $t('signup.profiling.otherDeclarations.tinDeclaration.addTin')
                  "
                  v-if="tinValues.length < 3"
                />
              </div>
              <div v-if="tinValues.length > 1">
                <NewButton
                  id="signup-declarations-remove-tin"
                  @on-click="removeTin()"
                  :text="
                    $t('signup.profiling.otherDeclarations.tinDeclaration.removeTin')
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </v-form>
      <NewButton
        id="next-step"
        class="signup-step-button"
        :class="{ 'signup-success-btn': signupStatus.completedProfiling }"
        :text="
          signupStatus.completedProfiling
            ? $t('signup.profiling.otherDeclarations.success')
            : $t('base.finish')
        "
        :loading="loading"
        :disabled="!validForm"
        @on-click="onAcceptDeclarationsClick()"
      />

      <div
        class="signup-cancel-container"
        @click="$router.push({ name: 'signupDashboard' })"
      >
        <p class="signup-cancel">
          {{ $t("signup.profiling.shared.cancel") }}
        </p>
      </div>

      <ReusableDialog
        v-model="errorDialog"
        :title="$t('signup.profiling.errors.title')"
        :text="$t('signup.profiling.errors.message')"
        @display="errorDialog = $event"
      >
        <template v-slot:buttonRow>
          <div class="signup-button-row">
            <div class="chat-btn">
              <NewButton :text="$t('base.goToChat')" @on-click="openChat()" />
            </div>

            <div class="keep-trying-btn">
              <NewButton
                :text="$t('base.keepTrying')"
                :loading="loading"
                @on-click="
                  errorDialog = false;
                  onAcceptDeclarationsClick();
                "
              />
            </div>
          </div>
        </template>
      </ReusableDialog>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import userSelects from "@/mixins/userSelects.js";
import ReusableDialog from "@/components/shared/ReusableDialog.vue";
import values from "@/helpers/values.js";
import { signupFunctions } from "@/mixins/signup.js";
import NewButton from "@/components/shared/NewButton.vue";

export default {
  name: "ProfilingOtherDeclarations",

  mixins: [values, signupFunctions, userSelects],

  components: { NewButton, ReusableDialog },

  data() {
    return {
      errorDialog: false,
      declarationAnswers: {},
      loading: false,
      tinValues: [{ country: null, tin: null }],
      countryRules: [
        v => !!v || this.$t("signup.shared.errors.mustComplete"),
        v => this.countries.some(el => el.code === v) ||
          this.$t("signup.shared.errors.selectCountry"),
      ],
      mustCompleteRules: [
        v => !!v || this.$t("signup.shared.errors.mustComplete"),
      ],
      validForm: false,
    };
  },

  computed: {
    ...mapGetters(["signupDeclarations", "profilingScore", "signupEmail", "signupStatus"]),
  },

  methods: {
    ...mapActions(["setProfilingCustomerDeclarations"]),

    async onAcceptDeclarationsClick() {
      if (this.signupStatus.completedProfiling || this.loading) return;
      this.loading = true;
      this.setProfilingCustomerDeclarations({ tinValues: this.tinValues });
      const payload = {
        ...this.declarations,
        profiling: this.profilingScore,
      };
      const setProfilingFlowDataResponse = await this.sendProfilingData(
        this.signupEmail,
        payload,
      );
      this.loading = false;
      if (setProfilingFlowDataResponse.error) {
        this.errorDialog = true;
      } else {
        await new Promise(resolve => setTimeout(resolve, 3000));
        this.$router.push({ name: "signupDashboard" });
      }
    },

    openChat() {
      this.$intercom.showNewMessage(this.$t("signup.profiling.errors.intercom"));
    },

    addTin() {
      this.tinValues.push({ country: null, tin: null });
    },

    removeTin() {
      this.tinValues.pop();
    },

    validateRef(refsArray) {
      // if there is no refs (because the declaration isCrs is false) we return that the field is valid
      if (refsArray.length === 0) return true;
      return refsArray[0].validate();
    },
  },

  mounted() {
    this.declarationAnswers = Object.fromEntries(
      this.otherDeclarations.map(otherDeclaration => [
        otherDeclaration.declaration,
        false,
      ]),
    );
  },
};
</script>

<style lang="scss" scoped>
.other-declaration-text {
  display: flex;
  margin-top: 40px;
  margin-bottom: 20px;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 160%;
  color: #333333;
}

.other-declaration-option-container {
  display: flex;
}

.other-declaration-option {
  background-color: #ffffff;
  border-radius: 10px;
  height: 60px;
  width: 100%;
  padding: 0 20px;
  display: flex;
  align-items: center;

  span {
    font-weight: 500;
    font-size: 14px;
  }

  &:hover {
    background-color: #fafafa;
    cursor: pointer;
  }
}

.other-declaration-option:first-of-type {
  margin-right: 10px;
}

.selected-other-declaration {
  border: 1px solid #000000 !important;
}

.unselected-other-declaration {
  border: 1px solid #e2e0e0 !important;
  span {
    color: #828282 !important;
  }
}

.tin-declaration {
  margin-top: 20px;
}

.tin-declaration + .tin-declaration {
  margin-top: 20px;
}

.signup-tin-buttons-wrapper {
  max-width: 100% !important;
  margin-top: 40px;
  display: flex;
  justify-content: space-between;
  gap: 0 10px;
}

.signup-tin-buttons-wrapper > * {
  width: 100% !important;
}

.signup-success-btn {
  background-color: #6d48ff !important;
  border: unset;
}
</style>
